import { useQueryClient } from "@tanstack/react-query"
import { PROPERTY_EVENT_NAMES } from "@zplanem/responses"
import { useEffect } from "react"
import { queryKeys } from "../../lib/query-keys"
import { getPusherClient } from "../realtime/pusher-client"

/**
 * Live-refresh the properties list when any of the user's properties changes
 * its name/description (ZPL-186). The dashboard isn't bound to a single
 * property's channel, so it subscribes to each visible property's channel for
 * `property.updated` and invalidates the list query when one fires.
 */
export function useDashboardPropertyChannels(propertyIds: string[]): void {
	const queryClient = useQueryClient()
	// Re-subscribe only when the id SET changes; the array identity churns each
	// render, so key the effect on the joined ids.
	const key = propertyIds.join(",")
	// biome-ignore lint/correctness/useExhaustiveDependencies: `propertyIds` is tracked via `key`.
	useEffect(() => {
		if (propertyIds.length === 0) return
		const pusher = getPusherClient()
		const invalidate = () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.properties(),
				exact: true,
			})
		const subs = propertyIds.map((id) => {
			const name = `private-property-${id}`
			const channel = pusher.subscribe(name)
			for (const event of PROPERTY_EVENT_NAMES) channel.bind(event, invalidate)
			return name
		})
		return () => {
			for (const name of subs) {
				const channel = pusher.channel(name)
				for (const event of PROPERTY_EVENT_NAMES) {
					channel?.unbind(event, invalidate)
				}
				pusher.unsubscribe(name)
			}
		}
	}, [key, queryClient])
}
